import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { useChatActivityTracking } from '../features/assistant/utils/tracking';
import { useChatStore } from '../stores/chatStore';

// import type { ChatApiResponse } from '../features/assistant/types/api';
import type { UseMutateFunction, UseMutationOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

const client = axios.create({
  baseURL: 'https://kym8bzsib4.eu-west-1.awsapprunner.com/',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

interface MessagePayload {
  message: string;
  session_id?: string;
}

export type MutationOptionsType<TData, TVariables> = Omit<
  UseMutationOptions<TData, AxiosError, TVariables>,
  'mutationFn'
>;

export type AssistantChatMutationType = UseMutateFunction<
  ChatApiResponse,
  AxiosError<unknown>,
  MessagePayload,
  unknown
>;

export const useAssistantChat = (options?: MutationOptionsType<ChatApiResponse, MessagePayload>) => {
  const addAIResponse = useChatStore.use.addAIResponse();
  const session_id = useChatStore.use.sessionId();
  const setSessionId = useChatStore.use.setSessionId();
  const { trackEvent } = useChatActivityTracking();

  return useMutation<ChatApiResponse, AxiosError, MessagePayload>({
    mutationFn: async (payload): Promise<ChatApiResponse> => {
      const response = await client.post<ChatApiResponse>('api/v1/chat', {
        ...payload,
        session_id,
      });
      return response.data;
    },
    onMutate: (payload) => {
      console.log('Sending message:', payload.message);
      trackEvent('input_submit', { input: payload.message, sessionId: session_id ?? undefined });
    },
    onError: (error) => {
      console.error('Error sending message:', error);
    },
    onSuccess: (data) => {
      console.log('Message sent successfully:', data);
      trackEvent('response_received', {
        response: data.response,
        sessionId: data.session_id ?? session_id,
      });
      addAIResponse(data);
      if (data.session_id && !session_id) {
        setSessionId(data.session_id);
      }
    },
    ...options,
  });
};

export interface FinancialRiskCalculatorLineItem {
  age: number;
  value: number;
}

export interface FinancialRiskCalculator {
  income_line: FinancialRiskCalculatorLineItem[];
  expenses_line: FinancialRiskCalculatorLineItem[];
  short_fall_euros: number;
  bridgeable_months: number;
  unbridgeable_months: number;
  inputs: {
    aov_enabled: boolean;
    aov_payout_period: number;
    aov_waiting_period: number;
    current_age: number;
    disabled_age: number;
    monthly_expenses: number;
    monthly_income: number;
    retirement_age: number;
    total_savings: number;
  };
}

export interface PensionCalculatorInputs {
  monthly_contribution: number;
  annual_roi: number;
  inflation: number;
  retirement_age: number;
  target_retirement_duration_years: number;
  current_age: number;
}

export interface PensionCalculator {
  accumulated_capital: number;
  monthly_income: number;
  inputs: PensionCalculatorInputs;
}
export interface ChatApiResponse {
  session_id: string; // session id for the chat
  response: string; // response from the assistant
  user_vars: {
    [key: string]: string | number;
  };
  calculated_vars: {
    years_until_retirement: number;
    months_until_retirement: number;
    bridgeable_months: number;
    unbridgeable_months: number;
    disabled_age: number;
    recommended_waiting_period: number;
    short_fall_euros: number;
    [key: string]: unknown;
  };
  slides?: Array<PresentationSlide>;
}

export interface PresentationSlide {
  content: string; // markdown content for the slide
  narration: string; // narration text for the slide
  questions: string[]; // questions related to the slide
  theme: 'white' | 'dark' | 'purple' | 'green' | 'lightGreen' | 'putty'; // theme of the slide
  image?: string; // optional image URL for the slide
}
